/**
 * Dashboard financiero.
 *
 * No calcula nada por su cuenta: consume las magnitudes que los estados
 * exportan. El tablero de la hoja de cálculo tomaba `ERI!J9` —Ingresos
 * Operativos, 55,000— como base de todos sus porcentajes en lugar de `ERI!J12`
 * —TOTAL INGRESOS, 60,256,097.21—, y por eso mostraba «Costo de Ventas
 * 59,553.9 %». Aquí la base se declara una vez, en el mapping, y se muestra.
 *
 * Qué cambió, y por qué
 * ---------------------
 * Era una tabla pequeña, siete tarjetas iguales y otra tabla pequeña, cada
 * bloque en una tira de ancho completo con la mitad derecha vacía: la
 * información estaba toda, y ninguna pregunta se contestaba de un vistazo. Las
 * cifras siguen aquí —abajo, íntegras y con su porcentaje— pero dejan de ser lo
 * primero:
 *
 *   * cuatro cifras de resultado con su margen, arriba;
 *   * la evolución mensual, que es lo que una columna de totales no puede
 *     decir;
 *   * la estructura patrimonial en barras, porque comparar dos barras apoyadas
 *     en la misma línea base es la comparación que mejor hace el ojo, y siete
 *     tarjetas con siete números es la que peor;
 *   * los indicadores contra su rango, en vez de contra una frase.
 *
 * Los meses salen de `pkg.monthly`, que ya viaja en el mismo contexto: esta
 * pantalla no pide un solo dato nuevo al servidor.
 */

import { Link } from "react-router-dom";
import { StatTile } from "../../components/StatTile";
import { BarrasMes } from "../../components/viz/BarrasMes";
import { BarrasComposicion, type Magnitud } from "../../components/viz/BarrasComposicion";
import { Balas, type Bala } from "../../components/viz/Bala";
import {
  formatMillions,
  formatMoney,
  moneyClass,
  formatPercentOfBase,
  formatRatio,
  toNumber,
} from "../../components/format";
import type { Ratio } from "../../api/types";
import { useClosing } from "./closingContext";
import { StatementHead } from "./shared";

/** Las cuatro que encabezan, en el orden en que se leen en el ERI. */
const CABECERA = [
  { id: "ERI_TOTAL_INGRESOS", label: "Ingresos" },
  { id: "ERI_UTILIDAD_BRUTA", label: "Utilidad bruta" },
  { id: "ERI_UTILIDAD_OPERACION", label: "Utilidad operativa" },
  { id: "ERI_RESULTADO", label: "Utilidad neta" },
];

/** Tres series, no cinco: con más, las barras de un mes dejan de compararse. */
const SERIES_MES = [
  { id: "ERI_TOTAL_INGRESOS", label: "Ingresos", slot: 1 as const },
  { id: "ERI_TOTAL_COSTOS", label: "Costos", slot: 2 as const },
  { id: "ERI_RESULTADO", label: "Resultado", slot: 3 as const },
];

export function FinancialDashboard() {
  const { package: pkg } = useClosing();
  const tablero = pkg.dashboard;

  const porId = new Map(tablero.summary.map((item) => [item.id, item]));
  const posicion = new Map(tablero.position.map((item) => [item.id, item]));

  const meses = pkg.monthly?.months ?? [];
  const filasMes = new Map((pkg.monthly?.rows ?? []).map((r) => [r.line_id, r]));
  const series = SERIES_MES.filter((s) => filasMes.has(s.id)).map((s) => ({
    ...s,
    valores: meses.map((mes) => filasMes.get(s.id)?.months[mes] ?? null),
  }));

  const composicion: Magnitud[] = [
    {
      id: "activo",
      label: "Activo",
      value: posicion.get("ESF_ACTIVO_TOTAL")?.value ?? null,
      slot: 1,
      partes: [
        { label: "Corriente", value: posicion.get("ESF_ACTIVO_CORRIENTE")?.value ?? null },
        { label: "No corriente", value: posicion.get("ESF_ACTIVO_NO_CORRIENTE")?.value ?? null },
      ],
    },
    {
      id: "pasivo",
      label: "Pasivo",
      value: posicion.get("ESF_PASIVO_TOTAL")?.value ?? null,
      slot: 2,
      partes: [
        { label: "Corriente", value: posicion.get("ESF_PASIVO_CORRIENTE")?.value ?? null },
        { label: "No corriente", value: posicion.get("ESF_PASIVO_NO_CORRIENTE")?.value ?? null },
      ],
    },
    {
      id: "patrimonio",
      label: "Patrimonio",
      value: posicion.get("ESF_PATRIMONIO")?.value ?? null,
      slot: 3,
    },
  ];

  return (
    <>
      <StatementHead
        title="Dashboard Financiero"
        subtitle={`del ${pkg.period_from} al ${pkg.period_to}`}
      />

      <div className="tiles">
        {CABECERA.map((def) => {
          const item = porId.get(def.id);
          if (!item) return null;
          return (
            <StatTile
              key={def.id}
              mass
              label={def.label}
              value={formatMillions(item.value)}
              note={
                item.share === null || item.share === undefined
                  ? `USD ${formatMoney(item.value)}`
                  : `${formatPercentOfBase(item.share)} sobre ingresos`
              }
            />
          );
        })}
      </div>

      <div className="pill-row tiles-footnote">
        <span className="small muted">
          Porcentajes sobre <span className="mono">{tablero.share_base.id}</span> ={" "}
          {formatMoney(tablero.share_base.value)}, la misma cifra que presenta el
          Estado del Resultado Integral.
        </span>
      </div>

      {series.length > 0 && meses.length > 0 && (
        <div className="card">
          <h2>Evolución mensual de resultados</h2>
          <BarrasMes meses={meses} series={series} />
          <p className="small muted">
            El desglose completo, renglón a renglón, está en{" "}
            <Link to="/closing/monthly">Detalle ERI Mensual</Link>.
          </p>
        </div>
      )}

      <div className="card-columns">
        <div className="card">
          <h2>Composición financiera</h2>
          <BarrasComposicion filas={composicion} />
        </div>

        <div className="card">
          <h2>Indicadores clave</h2>
          <Balas indicadores={tablero.kpis.map(bala)} />
        </div>
      </div>

      <div className="card">
        <h2>Resumen de resultados</h2>
        <div className="table-wrap documento">
          <table>
            <thead>
              <tr>
                <th scope="col">Concepto</th>
                <th scope="col" className="num">Importe</th>
                <th scope="col" className="num">% sobre ingresos</th>
              </tr>
            </thead>
            <tbody>
              {tablero.summary.map((item) => (
                <tr key={item.id}>
                  <td>{item.label}</td>
                  <td className={moneyClass(item.value)}>{formatMoney(item.value)}</td>
                  <td className="num">
                    {item.share === null || item.share === undefined
                      ? "—"
                      : formatPercentOfBase(item.share)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/**
 * Un indicador, como bala.
 *
 * La proporción del riel es una LECTURA, no un juicio: un margen del 47.7 % se
 * dibuja al 47.7 % del riel, y una razón se dibuja contra el doble de su valor
 * ideal habitual para que quepa el rango entero. No se colorea nada aquí: el
 * estado lo pone `estado`, y solo lo pone quien tiene un umbral formal que
 * comparar. La liquidez de esta empresa es 28 contra un ideal de «1.50 a 2.00»
 * — pintarla de verde afirmaría que veintiocho veces el pasivo corriente es
 * bueno, y eso no lo dice el rango, lo diría la pantalla.
 */
function bala(kpi: Ratio): Bala {
  const texto = formatRatio(kpi.value, kpi.format);
  // `toNumber` y no `Number.isFinite` a secas: el importe puede llegar como
  // cadena, y una cadena nunca es finita — la barra no se dibujaba nunca.
  const valor = toNumber(kpi.value);
  const proporcion =
    valor !== undefined && kpi.format === "percent" ? Math.abs(valor) : null;
  return { id: kpi.id, label: kpi.label, texto, proporcion, ideal: kpi.ideal };
}
